import { SocketProvider } from '../context/socketContext';
import Login from '../pages/auth/login/Login';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import { clearNotification } from '../store/action/notifications';
import { clearUserInfo, fetchUserInfo, setAuthToken, setIsOnboardingCompleted } from '../store/action/userInfo';
import PageLoader from '../component/PageLoader';

// This component sits ABOVE every nested module route (see router/index.js —
// one <PrivateRoute><Layout/></PrivateRoute> wraps the whole `/user/:suburl/*`
// tree, with modules as children rendered through Layout's <Outlet/>). It
// therefore mounts exactly once per hard refresh or fresh entry into the
// authenticated app, and stays mounted while the user switches between
// modules — making it the one place a "run this once per session" fetch
// belongs.
const PrivateRoute = ({ children }) => {
	const location = useLocation();
	const { suburl } = useParams();
	const { userOnboardingMe, checkSsoSession } = useAuth();
	const dispatch = useDispatch();
	const navigate = useNavigate();

	// Selected as PRIMITIVES, one at a time, not as `state.userInfo`.
	//
	// Selecting the whole slice returns a NEW object reference every time
	// anything in it changes — including `userInfo.data`, which the fetch below
	// writes. That re-rendered this component on its own fetch, and since
	// useAuth() hands back a fresh `userOnboardingMe` function on every render,
	// the effect's dependency array changed too and it fired again: fetch →
	// re-render → fetch, hammering /user/info forever. Comparing two strings
	// and a boolean by value breaks that cycle at the source.
	const authToken = useSelector((state) => state.userInfo.authToken);
	const isOnboardingCompleted = useSelector((state) => state.userInfo.isOnboardingCompleted);

	// Synchronize localStorage suburl with the current route parameter immediately
	if (suburl && localStorage.getItem('suburl') !== suburl) {
		localStorage.setItem('suburl', suburl);
	}

	// Second, independent guard. Even if something re-renders this component
	// for an unrelated reason, the authoritative user fetch is a once-per-mount
	// job — PrivateRoute mounts once per hard refresh and stays mounted across
	// module navigation, so there is never a legitimate reason to repeat it.
	const hasSyncedUser = useRef(false);
	const hasCheckedSso = useRef(false);

	// Synchronously initialize: If authToken and isOnboardingCompleted are already valid,
	// do NOT block the UI or show PageLoader; allow immediate rendering without flicker.
	const [isChecking, setIsChecking] = useState(!authToken || !isOnboardingCompleted);
	const isLoginRoute = location.pathname === `/user/${suburl}`;

	useEffect(() => {
		const checkOnboarding = async () => {
			if (isLoginRoute) {
				setIsChecking(false);
				return;
			}

			let currentAuthToken = authToken;

			// Check SSO session at most once on initial mount if not already authenticated,
			// preventing re-trigger render loops when authToken is updated.
			if (!currentAuthToken && !hasCheckedSso.current) {
				hasCheckedSso.current = true;
				try {
					const ssoData = await checkSsoSession(suburl);
					if (ssoData?.success && ssoData?.authenticated && ssoData?.token) {
						currentAuthToken = ssoData.token;
					} else if ((ssoData?.authenticated === false && !ssoData?.error) || ssoData?.explicitLogout) {
						// Single Sign-Out: Session was terminated on another platform (e.g. POS)
						localStorage.clear();
						dispatch(clearNotification());
						dispatch(setAuthToken({ authToken: null }));
						dispatch(clearUserInfo());
						dispatch(setIsOnboardingCompleted({ isOnboardingCompleted: false }));
						setIsChecking(false);
						navigate(`/user/${suburl}`);
						return;
					}
				} catch (ssoError) {
					console.error('SSO session check failed:', ssoError);
				}
			}

			if (!currentAuthToken) {
				setIsChecking(false);
				return;
			}

			// Background user data sync without blocking UI if user is already authenticated
			try {
				if (!hasSyncedUser.current) {
					hasSyncedUser.current = true;
					await dispatch(fetchUserInfo());
				}
			} catch (err) {
				console.error('Failed to refresh user info:', err);
			}

			if (isOnboardingCompleted) {
				setIsChecking(false);
				return;
			}

			try {
				await userOnboardingMe();
			} catch (err) {
				console.error('Onboarding check failed:', err);
			} finally {
				setIsChecking(false);
			}
		};

		checkOnboarding();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOnboardingCompleted, isLoginRoute, userOnboardingMe, checkSsoSession, suburl, dispatch, navigate]);

	// This gate covers the whole authenticated app on a hard refresh, so it is
	// the loader users see most often. It previously used Tailwind utilities
	// (flex / items-center / justify-center / min-h-screen / text-lg) -- this
	// project has no Tailwind, only Bootstrap 5, so none of those classes
	// existed and the div collapsed to unstyled text in the top-left corner.
	// PageLoader is the spinner the rest of the app already uses.
	if (isChecking) {
		return <PageLoader />;
	}

	// If the user navigates to the bare `/user/:suburl` login route while already authenticated,
	// redirect cleanly to `/user/:suburl/home` rather than briefly flashing the Login form.
	if (isLoginRoute && authToken && isOnboardingCompleted) {
		return <Navigate to={`/user/${suburl}/home`} replace />;
	}

	if (!authToken || isLoginRoute) {
		return <Login />;
	}

	if (!isOnboardingCompleted) {
		return null;
	}

	return <SocketProvider>{children}</SocketProvider>;
};

export default PrivateRoute;
